/**
 * AFP Product Page Web Components
 * - <afp-gallery-thumbs>: thumbnail click/keyboard -> main image swap
 * - <afp-product-ticker>: duplicates content for seamless loop
 * - <afp-variant-picker-enhancer>: marks sold-out variants with strikethrough
 *
 * Depends on afp-product.css for visual states.
 * ES2020 modules — no bundler.
 */

class AfpGalleryThumbs extends HTMLElement {
  connectedCallback() {
    this.thumbs = Array.from(this.querySelectorAll('[data-afp-thumb]'));
    this.mainImg = document.querySelector(
      `[data-afp-main-img="${this.dataset.target}"]`
    );
    this.mainCaption = document.querySelector('[data-afp-main-caption]');
    if (!this.thumbs.length || !this.mainImg) return;

    this.thumbs.forEach((thumb, idx) => {
      thumb.setAttribute('tabindex', '0');
      thumb.setAttribute('role', 'button');
      thumb.addEventListener('click', () => this.activate(idx));
      thumb.addEventListener('keydown', (e) => this.onKey(e, idx));
    });
  }

  activate(idx) {
    const thumb = this.thumbs[idx];
    if (!thumb) return;
    const src = thumb.dataset.fullSrc || thumb.src;
    this.mainImg.src = src;
    const alt = thumb.getAttribute('alt');
    if (alt) this.mainImg.alt = alt;
    if (this.mainCaption) {
      this.mainCaption.textContent = thumb.dataset.fullCaption || 'Model height: [altezza] · Size worn: [taglia]';
    }
    this.thumbs.forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    this.dispatchEvent(
      new CustomEvent('afp:media-change', {
        detail: { index: idx, src },
        bubbles: true,
      })
    );
  }

  onKey(e, idx) {
    const last = this.thumbs.length - 1;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.activate(idx);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const next = idx === last ? 0 : idx + 1;
      this.thumbs[next].focus();
      this.activate(next);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = idx === 0 ? last : idx - 1;
      this.thumbs[prev].focus();
      this.activate(prev);
    }
  }
}

/* ─────────────────────── Mobile Gallery Carousel ─────────────────────── */
class AfpMobileGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-afp-gallery-track]');
    this.dots = Array.from(this.querySelectorAll('.afp-mobile-gallery__dot'));
    this.slides = Array.from(this.querySelectorAll('.afp-mobile-gallery__slide'));
    if (!this.track || this.slides.length <= 1) return;

    this.currentIndex = 0;

    // Dot click navigation
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        this.goTo(idx);
      });
    });

    // Use IntersectionObserver for performant scroll tracking
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const idx = this.slides.indexOf(entry.target);
            if (idx !== -1) this.setActive(idx);
          }
        });
      },
      { root: this.track, threshold: 0.5 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  goTo(idx) {
    const slide = this.slides[idx];
    if (!slide) return;
    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth',
    });
  }

  setActive(idx) {
    if (this.currentIndex === idx) return;
    this.currentIndex = idx;
    this.dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === idx);
    });
  }
}

class AfpProductTicker extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.afp-product-ticker__track');
    if (!this.track) return;
    const original = this.track.innerHTML;
    this.track.innerHTML = original + original;
  }
}

class AfpVariantPickerEnhancer extends HTMLElement {
  connectedCallback() {
    this.scan();
    this.bindRadios();
    this.observer = new MutationObserver(() => {
      this.scan();
      this.bindRadios();
    });
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  scan() {
    const disabled = this.querySelectorAll(
      'input[type="radio"][disabled], button[type="button"][disabled], .variant-option__button-label[aria-disabled="true"]'
    );
    disabled.forEach((el) => {
      const target = el.tagName === 'INPUT' ? el.nextElementSibling : el;
      if (target) target.classList.add('afp-size-btn--soldout');
    });
  }

  bindRadios() {
    const radios = this.querySelectorAll('.afp-variant-option__btn input[type="radio"]');
    radios.forEach((input) => {
      if (input.dataset.afpBound) return;
      input.dataset.afpBound = '1';
      input.addEventListener('change', () => this.onChange());
    });
  }

  onChange() {
    const labels = this.querySelectorAll('.afp-variant-option__btn');
    labels.forEach((l) => l.classList.remove('is-selected'));
    this.querySelectorAll('.afp-variant-option__btn input[type="radio"]:checked').forEach((i) => {
      const lab = i.closest('.afp-variant-option__btn');
      if (lab) lab.classList.add('is-selected');
    });

    const selected = {};
    this.querySelectorAll('.afp-variant-option input[type="radio"]:checked').forEach((i) => {
      const m = (i.name || '').match(/options\[(.+?)\]/);
      if (m) selected[m[1]] = i.value;
    });

    const variantId = this.findVariantId(selected);
    if (!variantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());

    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      let idInput = form.querySelector('input[name="id"]');
      if (!idInput) {
        idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        form.appendChild(idInput);
      }
      idInput.value = variantId;
    }
  }

  findVariantId(selected) {
    const data = window.AfpProductVariants;
    if (!data || !data.variants) return null;
    const optionNames = data.options || [];
    const wanted = optionNames.map((n) => selected[n]);
    const match = data.variants.find((v) => {
      return v.options.every((opt, idx) => opt === wanted[idx]);
    });
    return match ? match.id : null;
  }
}

class AfpPdpBadgeLogic {
  constructor() {
    if (!document.querySelector('[data-afp-pdp-badge]')) return;
    this.handler = (event) => {
      const variant = event?.detail?.resource;
      if (!variant) return;
      this.toggle(variant.available === false);
    };
    document.addEventListener('variant:update', this.handler);
    document.addEventListener('variant:selected', this.handler);
  }

  toggle(soldOut) {
    const badges = document.querySelectorAll('[data-afp-pdp-badge]');
    badges.forEach((badge) => {
      if (soldOut) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }
}

class AfpSizeChartLogic {
  constructor() {
    this.trigger = document.querySelector('[data-afp-size-chart-trigger]');
    this.modal = document.querySelector('[data-afp-size-chart-modal]');
    this.closeBtn = document.querySelector('[data-afp-size-chart-close]');

    if (!this.trigger || !this.modal) return;

    this.trigger.addEventListener('click', () => {
      this.modal.showModal();
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        this.modal.close();
      });
    }

    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.modal.close();
    });
  }
}

class AfpMeasurementUnits {
  constructor() {
    this.selector = document.querySelector('[data-afp-unit-selector]');
    this.tables = Array.from(document.querySelectorAll('[data-afp-measurement-table]'));
    if (!this.selector || !this.tables.length) return;

    this.buttons = Array.from(this.selector.querySelectorAll('[data-afp-unit]'));
    this.cells = [];

    this.tables.forEach((table) => {
      const headers = Array.from(table.querySelectorAll('thead th'));
      const measurementColumns = headers
        .map((header, index) => ({ label: header.textContent.trim().toLowerCase(), index }))
        .filter((column) => column.label !== 'size' && column.label !== 'it')
        .map((column) => column.index);

      table.querySelectorAll('tbody tr').forEach((row) => {
        const rowCells = Array.from(row.cells);
        measurementColumns.forEach((index) => {
          const cell = rowCells[index];
          if (!cell) return;
          const cmDisplay = cell.textContent.trim();
          const cmValue = Number(cmDisplay.replace(',', '.'));
          if (!Number.isFinite(cmValue)) return;
          cell.dataset.afpCmDisplay = cmDisplay;
          cell.dataset.afpCmValue = String(cmValue);
          this.cells.push(cell);
        });
      });
    });

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.setUnit(button.dataset.afpUnit));
    });
  }

  setUnit(unit) {
    if (unit !== 'cm' && unit !== 'in') return;
    this.cells.forEach((cell) => {
      if (unit === 'cm') {
        cell.textContent = cell.dataset.afpCmDisplay;
      } else {
        cell.textContent = (Number(cell.dataset.afpCmValue) / 2.54).toFixed(1);
      }
    });
    this.buttons.forEach((button) => {
      const active = button.dataset.afpUnit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }
}

class AfpStickyCtaLogic {
  constructor() {
    this.stickyBar = document.querySelector('[data-afp-sticky-cta]');
    this.buyButtonsBlock = document.querySelector('.afp-product__buy');
    this.mainFormBtn = document.querySelector('.afp-product__buy .product-form__buttons button[type="submit"]') || document.querySelector('.afp-product__buy button[type="submit"]');
    this.stickyTrigger = document.querySelector('[data-afp-sticky-trigger]');

    if (!this.stickyBar || !this.buyButtonsBlock) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && entry.boundingClientRect.y < 0) {
            this.stickyBar.classList.add('is-visible');
          } else {
            this.stickyBar.classList.remove('is-visible');
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(this.buyButtonsBlock);

    if (this.stickyTrigger && this.mainFormBtn) {
      this.stickyTrigger.addEventListener('click', () => {
        this.mainFormBtn.click();
      });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AfpSizeChartLogic();
  new AfpMeasurementUnits();
  new AfpStickyCtaLogic();
  new AfpPdpBadgeLogic();
});

if (!customElements.get('afp-gallery-thumbs')) {
  customElements.define('afp-gallery-thumbs', AfpGalleryThumbs);
}
if (!customElements.get('afp-mobile-gallery')) {
  customElements.define('afp-mobile-gallery', AfpMobileGallery);
}
if (!customElements.get('afp-product-ticker')) {
  customElements.define('afp-product-ticker', AfpProductTicker);
}
if (!customElements.get('afp-variant-picker-enhancer')) {
  customElements.define('afp-variant-picker-enhancer', AfpVariantPickerEnhancer);
}
